import {
  CalculatedTaskStatus,
  type DashboardTaskGroup,
  type Day
} from '@lmc/ts-common'
import { Heading, StatusBadge } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { formatDate, getPartOfDay } from 'common/utils'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { toDosConstants } from 'locale/toDos'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import useTaskGroups, { type TaskGroupCursor } from '../hooks/useTaskGroups'
import type { TaskFiltersValue } from './TaskFilters'
import TaskGroup from './TaskGroup'

type DayCardProps = {
  day: Day
  filterParams?: TaskFiltersValue
  refetchDailyTasks: () => Promise<any>
  testId?: string
}

const getCursor = (taskGroups: DashboardTaskGroup[]): TaskGroupCursor => {
  const lastTaskGroupIndex = taskGroups.length
  const lastTaskGroup = taskGroups[lastTaskGroupIndex - 1]

  return {
    date: lastTaskGroup.dateTime,
    recurringTaskID: lastTaskGroup.recurringTaskID
  }
}

const DayCard = ({
  day,
  filterParams,
  refetchDailyTasks,
  testId
}: DayCardProps) => {
  const { t } = useTranslation(NAMESPACE.TODOS)

  const [dayTaskGroups, setDayTakGroups] = useState(day.taskGroups)

  const [usedTaskGroups, setUsedTaskGroups] = useState(dayTaskGroups)

  const [cursor] = useState(getCursor(day.taskGroups))

  const {
    taskGroups: paginatedTaskGroups,
    isLoading: taskGroupsLoading,
    fetchNextPage,
    hasNextPage,
    refetch
  } = useTaskGroups({
    ...filterParams,
    initialCursor: cursor
  })

  useEffect(() => {
    setDayTakGroups(day.taskGroups)
  }, [day.taskGroups])

  useEffect(() => {
    setUsedTaskGroups(dayTaskGroups.concat(paginatedTaskGroups))
  }, [dayTaskGroups, paginatedTaskGroups])

  const taskGroupsByPartOfDay = useMemo<{
    [partOfDay: string]: DashboardTaskGroup[]
  }>(() => {
    const groupedTaskGroups: { [partOfDay: string]: DashboardTaskGroup[] } = {}

    const computedTaskGroups = usedTaskGroups

    computedTaskGroups.forEach((taskGroup) => {
      const partOfDay = getPartOfDay(taskGroup.dateTime)
      if (!groupedTaskGroups[partOfDay]) {
        groupedTaskGroups[partOfDay] = []
      }
      groupedTaskGroups[partOfDay].push(taskGroup)
    })

    return groupedTaskGroups
  }, [usedTaskGroups])

  const loadMoreTaskGroups = () => {
    fetchNextPage()
  }

  const hasMoreTaskGroups = () => {
    if (paginatedTaskGroups) {
      return day.hasMoreTodos && hasNextPage
    }

    return day.hasMoreTodos
  }

  const generalRefetch = async () => {
    await refetchDailyTasks()
    await refetch()
  }

  return (
    <Wrapper data-testid={testId || 'day-card'}>
      <ToDoHeader data-testid={testId ? `${testId}-header` : 'day-card-header'}>
        <DateContainer
          data-testid={
            testId ? `${testId}-date-container` : 'day-card-date-container'
          }
        >
          <DateText
            level={3}
            data-testid={testId ? `${testId}-date-text` : 'day-card-date-text'}
          >
            {dayjs(day.date).format('D MMMM YYYY')}
          </DateText>
          <DayText
            level={1}
            data-testid={testId ? `${testId}-day-text` : 'day-card-day-text'}
          >
            {formatDate(dayjs(day.date))}
          </DayText>
        </DateContainer>
        <StatusesContainer
          data-testid={
            testId
              ? `${testId}-statuses-container`
              : 'day-card-statuses-container'
          }
        >
          {sortedCounts
            .filter((status) => Object.keys(day.counts).includes(status))
            .map((statusType) => (
              <StatusBadgeStyled
                key={statusType}
                status={statusType as CalculatedTaskStatus}
                count={day.counts[statusType as CalculatedTaskStatus]}
                leftText={false}
                showStatus
                data-testid={
                  testId
                    ? `${testId}-status-badge-${statusType}`
                    : `day-card-status-badge-${statusType}`
                }
              />
            ))}
        </StatusesContainer>
      </ToDoHeader>
      <ToDoContent
        data-testid={testId ? `${testId}-content` : 'day-card-content'}
      >
        {Object.entries(taskGroupsByPartOfDay).map(
          ([partOfDay, taskGroups]) => (
            <div
              key={partOfDay}
              data-testid={
                testId
                  ? `${testId}-part-of-day-${partOfDay.toLowerCase().replace(/\s+/g, '-')}`
                  : `day-card-part-of-day-${partOfDay.toLowerCase().replace(/\s+/g, '-')}`
              }
            >
              <SubtitleContainer
                data-testid={
                  testId
                    ? `${testId}-subtitle-container-${partOfDay.toLowerCase().replace(/\s+/g, '-')}`
                    : `day-card-subtitle-container-${partOfDay.toLowerCase().replace(/\s+/g, '-')}`
                }
              >
                <Subtitle
                  level={4}
                  weight="bold"
                  data-testid={
                    testId
                      ? `${testId}-subtitle-${partOfDay.toLowerCase().replace(/\s+/g, '-')}`
                      : `day-card-subtitle-${partOfDay.toLowerCase().replace(/\s+/g, '-')}`
                  }
                >
                  {partOfDay}
                </Subtitle>
              </SubtitleContainer>
              <TaskGroupsWrapper
                data-testid={
                  testId
                    ? `${testId}-task-groups-wrapper-${partOfDay.toLowerCase().replace(/\s+/g, '-')}`
                    : `day-card-task-groups-wrapper-${partOfDay.toLowerCase().replace(/\s+/g, '-')}`
                }
              >
                {taskGroups.map((taskGroup, index) => (
                  <TaskGroup
                    key={`${taskGroup.recurringTaskID}-${taskGroup.dateTime}`}
                    taskGroup={taskGroup}
                    filterParams={filterParams}
                    refetchDailyTasks={generalRefetch}
                    testId={
                      testId
                        ? `${testId}-task-group-${partOfDay.toLowerCase().replace(/\s+/g, '-')}-${index}`
                        : `day-card-task-group-${partOfDay.toLowerCase().replace(/\s+/g, '-')}-${index}`
                    }
                  />
                ))}
              </TaskGroupsWrapper>
            </div>
          )
        )}
      </ToDoContent>
      {hasMoreTaskGroups() && (
        <LoadMoreWrapper
          data-testid={
            testId
              ? `${testId}-load-more-wrapper`
              : 'day-card-load-more-wrapper'
          }
        >
          <Button
            isLoading={taskGroupsLoading}
            onClick={loadMoreTaskGroups}
            variant="text"
            data-testid={
              testId
                ? `${testId}-load-more-button`
                : 'day-card-load-more-button'
            }
          >
            {t(toDosConstants.LOAD_MORE)}
          </Button>
        </LoadMoreWrapper>
      )}
    </Wrapper>
  )
}

const sortedCounts: CalculatedTaskStatus[] = [
  CalculatedTaskStatus.pending,
  CalculatedTaskStatus.due,
  CalculatedTaskStatus.overdue,
  CalculatedTaskStatus.completed,
  CalculatedTaskStatus.missed,
  CalculatedTaskStatus.skipped
]

const TaskGroupsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StatusBadgeStyled = styled(StatusBadge)`
  padding: 4px 8px;
`

const LoadMoreWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;
`

const Wrapper = styled.div`
  border-radius: 16px;
  background-color: ${({ theme }) => theme.color.neutral.color11};
  padding: 24px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 20px;
  }
`

const ToDoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const DateText = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const DayText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const Subtitle = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const SubtitleContainer = styled.div`
  margin-bottom: 12px;
`

const StatusesContainer = styled.div`
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`

const ToDoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export default DayCard
